import React, { useEffect, useRef, useState } from "react";
import TimeSelect from "components/admin/TimeSelect";
import * as S from "./AdminFacilityDetail.styled";
import { facilityService, FacilityDetail } from "services/facilityService";
import { adminBuildingService } from "services/adminBuildingService";
import { adminThemeService } from "services/adminThemeService";
import {
  adminFacilityService,
  FacilityUpdateRequest,
} from "services/adminFacilityService";
import { Building, Theme } from "types";

interface Props {
  facilityId: number;
  onClose: () => void;
  onDeleted?: () => void;
}

const CATEGORIES = [
  { value: "", label: "선택하세요" },
  { value: "STUDY", label: "스터디" },
  { value: "SPORTS", label: "체육시설" },
  { value: "CULTURE", label: "문화공간" },
  { value: "PRINT_PC", label: "프린터/PC" },
];

const makeDefaultForm = (facility: FacilityDetail): FacilityUpdateRequest => ({
  name: facility.name ?? "",
  description: facility.description ?? "",
  purpose: facility.purpose ?? "",
  equipment: facility.equipment ?? "",
  caution: facility.caution ?? "",
  location: facility.location ?? "",
  thumbnailImage: facility.imageUrl ?? "",
  category: facility.category ?? "",
  operationTimes: (facility.operationTime ?? []).map((t) => ({
    name: t.name,
    startTime: t.startTime.slice(0, 5),
    endTime: t.endTime.slice(0, 5),
  })),
  images: facility.images ?? [],
  reservationPolicy: {
    reservable: facility.reservable ?? false,
    availableTimes: [],
  },
  buildingId: facility.building?.id ?? 0,
  themeIds: (facility.themes ?? []).map((t) => t.id),
});

const AdminFacilityDetail: React.FC<Props> = ({ facilityId, onClose, onDeleted }) => {
  const [facility, setFacility] = useState<FacilityDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [form, setForm] = useState<FacilityUpdateRequest | null>(null);

  const thumbInputRef = useRef<HTMLInputElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsLoading(true);
    facilityService
      .getFacilityDetail(facilityId)
      .then((data) => {
        setFacility(data);
        setSelectedImage(data.imageUrl ?? null);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [facilityId]);

  const allImages = facility
    ? [facility.imageUrl, ...(facility.images ?? [])].filter(Boolean)
    : [];

  const enterEdit = () => {
    if (!facility) return;
    const universityId = Number(localStorage.getItem("adminUniversityId"));
    adminBuildingService.getBuildings(universityId).then(setBuildings).catch(() => {});
    adminThemeService.getThemes(universityId).then(setThemes).catch(() => {});
    setForm(makeDefaultForm(facility));
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!form) return;
    setIsSaving(true);
    try {
      await adminFacilityService.updateFacility(facilityId, form);
      const updated = await facilityService.getFacilityDetail(facilityId);
      setFacility(updated);
      setSelectedImage(updated.imageUrl ?? null);
      setIsEditing(false);
    } catch {
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("시설을 삭제하시겠습니까?")) return;
    setIsDeleting(true);
    try {
      await adminFacilityService.deleteFacility(facilityId);
      onClose();
      onDeleted?.();
    } catch {
    } finally {
      setIsDeleting(false);
    }
  };

  /* ── form helpers ── */
  const setField = <K extends keyof FacilityUpdateRequest>(
    key: K,
    value: FacilityUpdateRequest[K]
  ) => setForm((prev) => prev && { ...prev, [key]: value });

  const addOpTime = () =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          operationTimes: [
            ...prev.operationTimes,
            { name: "", startTime: "09:00", endTime: "18:00" },
          ],
        }
    );

  const removeOpTime = (i: number) =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          operationTimes: prev.operationTimes.filter((_, idx) => idx !== i),
        }
    );

  const setOpTime = (
    i: number,
    key: "name" | "startTime" | "endTime",
    value: string
  ) =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          operationTimes: prev.operationTimes.map((t, idx) =>
            idx === i ? { ...t, [key]: value } : t
          ),
        }
    );

  const removeImage = (i: number) =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          images: prev.images.filter((_, idx) => idx !== i),
        }
    );

  const addAvailableTime = () =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          reservationPolicy: {
            ...prev.reservationPolicy,
            availableTimes: [
              ...prev.reservationPolicy.availableTimes,
              { startTime: "09:00", endTime: "18:00" },
            ],
          },
        }
    );

  const removeAvailableTime = (i: number) =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          reservationPolicy: {
            ...prev.reservationPolicy,
            availableTimes: prev.reservationPolicy.availableTimes.filter(
              (_, idx) => idx !== i
            ),
          },
        }
    );

  const setAvailableTime = (
    i: number,
    key: "startTime" | "endTime",
    value: string
  ) =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          reservationPolicy: {
            ...prev.reservationPolicy,
            availableTimes: prev.reservationPolicy.availableTimes.map((t, idx) =>
              idx === i ? { ...t, [key]: value } : t
            ),
          },
        }
    );

  const toggleTheme = (themeId: number) =>
    setForm(
      (prev) =>
        prev && {
          ...prev,
          themeIds: prev.themeIds.includes(themeId)
            ? prev.themeIds.filter((id) => id !== themeId)
            : [...prev.themeIds, themeId],
        }
    );

  const handleThumbUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploadingThumb(true);
    try {
      const url = await adminFacilityService.uploadImage(file);
      setField("thumbnailImage", url);
    } catch {
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploadingImg(true);
    try {
      const url = await adminFacilityService.uploadImage(file);
      setForm((prev) => prev && { ...prev, images: [...prev.images, url] });
    } catch {
    } finally {
      setUploadingImg(false);
    }
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.Panel onClick={(e) => e.stopPropagation()}>

        {/* 헤더 */}
        <S.PanelHeader>
          <S.PanelTitle>{isEditing ? "시설 수정" : "시설 상세정보"}</S.PanelTitle>
          <S.HeaderActions>
            {!isEditing && !isLoading && facility && (
              <>
                <S.DeleteButton onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? "삭제 중..." : "삭제"}
                </S.DeleteButton>
                <S.EditButton onClick={enterEdit}>수정</S.EditButton>
              </>
            )}
            <S.CloseButton onClick={onClose}>✕</S.CloseButton>
          </S.HeaderActions>
        </S.PanelHeader>

        {/* 바디 */}
        <S.PanelBody>
          {isLoading ? (
            <S.LoadingWrap>
              <S.LoadingText>불러오는 중...</S.LoadingText>
            </S.LoadingWrap>
          ) : !facility ? (
            <S.LoadingWrap>
              <S.LoadingText>정보를 불러올 수 없습니다.</S.LoadingText>
            </S.LoadingWrap>
          ) : isEditing && form ? (
            /* ── 수정 폼 ── */
            <>
              {/* 기본 정보 */}
              <S.Section>
                <S.SectionTitle>기본 정보</S.SectionTitle>
                <S.FormGroup>
                  <S.FormLabel>시설명</S.FormLabel>
                  <S.FormInput
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>카테고리</S.FormLabel>
                  <S.FormSelect
                    value={form.category}
                    onChange={(e) => setField("category", e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </S.FormSelect>
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>위치</S.FormLabel>
                  <S.FormInput
                    value={form.location}
                    onChange={(e) => setField("location", e.target.value)}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>썸네일 이미지</S.FormLabel>
                  <input
                    ref={thumbInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleThumbUpload}
                  />
                  <S.ThumbUploadWrap
                    hasImage={!!form.thumbnailImage}
                    onClick={() => !uploadingThumb && thumbInputRef.current?.click()}
                  >
                    {form.thumbnailImage ? (
                      <S.ThumbUploadImg src={form.thumbnailImage} alt="thumbnail" />
                    ) : (
                      <S.ThumbUploadPlaceholder>
                        <span style={{ fontSize: 28 }}>🖼️</span>
                        <span>클릭하여 이미지 업로드</span>
                      </S.ThumbUploadPlaceholder>
                    )}
                    <S.ThumbUploadOverlay>
                      {uploadingThumb ? "업로드 중..." : "클릭하여 변경"}
                    </S.ThumbUploadOverlay>
                  </S.ThumbUploadWrap>
                </S.FormGroup>
              </S.Section>

              {/* 설명 및 목적 */}
              <S.Section>
                <S.SectionTitle>설명 및 목적</S.SectionTitle>
                <S.FormGroup>
                  <S.FormLabel>설명</S.FormLabel>
                  <S.FormTextarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>목적</S.FormLabel>
                  <S.FormInput
                    value={form.purpose}
                    onChange={(e) => setField("purpose", e.target.value)}
                  />
                </S.FormGroup>
              </S.Section>

              {/* 비품 및 주의사항 */}
              <S.Section>
                <S.SectionTitle>비품 및 주의사항</S.SectionTitle>
                <S.FormGroup>
                  <S.FormLabel>비품</S.FormLabel>
                  <S.FormInput
                    value={form.equipment}
                    onChange={(e) => setField("equipment", e.target.value)}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>주의사항</S.FormLabel>
                  <S.FormTextarea
                    rows={2}
                    value={form.caution}
                    onChange={(e) => setField("caution", e.target.value)}
                  />
                </S.FormGroup>
              </S.Section>

              {/* 운영 시간 */}
              <S.Section>
                <S.SectionTitle>운영 시간</S.SectionTitle>
                {form.operationTimes.map((t, i) => (
                  <S.ArrayRow key={i}>
                    <S.FormInput
                      placeholder="구분 (예: 평일)"
                      value={t.name}
                      onChange={(e) => setOpTime(i, "name", e.target.value)}
                      style={{ flex: 2 }}
                    />
                    <TimeSelect
                      value={t.startTime}
                      onChange={(v) => setOpTime(i, "startTime", v)}
                    />
                    <TimeSelect
                      value={t.endTime}
                      onChange={(v) => setOpTime(i, "endTime", v)}
                    />
                    <S.RemoveButton onClick={() => removeOpTime(i)}>✕</S.RemoveButton>
                  </S.ArrayRow>
                ))}
                <S.AddButton onClick={addOpTime}>+ 운영 시간 추가</S.AddButton>
              </S.Section>

              {/* 이미지 */}
              <S.Section>
                <S.SectionTitle>이미지 목록</S.SectionTitle>
                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImgUpload}
                />
                <S.ImgGrid>
                  {form.images.map((img, i) => (
                    <S.ImgGridItem key={i}>
                      <S.ImgGridImg src={img} alt={`이미지 ${i + 1}`} />
                      <S.ImgGridRemove onClick={() => removeImage(i)}>✕</S.ImgGridRemove>
                    </S.ImgGridItem>
                  ))}
                  {uploadingImg && <S.ImgGridLoading>업로드 중</S.ImgGridLoading>}
                  <S.ImgAddBtn
                    disabled={uploadingImg}
                    onClick={() => imgInputRef.current?.click()}
                  >
                    +
                  </S.ImgAddBtn>
                </S.ImgGrid>
              </S.Section>

              {/* 예약 정책 */}
              <S.Section>
                <S.SectionTitle>예약 정책</S.SectionTitle>
                <S.CheckboxRow>
                  <S.CheckboxInput
                    type="checkbox"
                    id="reservable"
                    checked={form.reservationPolicy.reservable}
                    onChange={(e) =>
                      setField("reservationPolicy", {
                        ...form.reservationPolicy,
                        reservable: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="reservable">예약 가능</label>
                </S.CheckboxRow>
                {form.reservationPolicy.reservable && (
                  <>
                    <S.FormLabel style={{ marginTop: 8 }}>예약 가능 시간대</S.FormLabel>
                    {form.reservationPolicy.availableTimes.map((t, i) => (
                      <S.ArrayRow key={i}>
                        <TimeSelect
                          value={t.startTime}
                          onChange={(v) => setAvailableTime(i, "startTime", v)}
                        />
                        <span style={{ color: "#aaa", fontSize: 12, flexShrink: 0 }}>~</span>
                        <TimeSelect
                          value={t.endTime}
                          onChange={(v) => setAvailableTime(i, "endTime", v)}
                        />
                        <S.RemoveButton onClick={() => removeAvailableTime(i)}>
                          ✕
                        </S.RemoveButton>
                      </S.ArrayRow>
                    ))}
                    <S.AddButton onClick={addAvailableTime}>+ 시간대 추가</S.AddButton>
                  </>
                )}
              </S.Section>

              {/* 건물 */}
              <S.Section>
                <S.SectionTitle>건물</S.SectionTitle>
                <S.FormSelect
                  value={form.buildingId || ""}
                  onChange={(e) => setField("buildingId", Number(e.target.value))}
                >
                  <option value="">선택하세요</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </S.FormSelect>
              </S.Section>

              {/* 테마 */}
              <S.Section>
                <S.SectionTitle>테마</S.SectionTitle>
                <S.ThemeList>
                  {themes.length === 0 ? (
                    <S.EmptyText>테마가 없습니다.</S.EmptyText>
                  ) : (
                    themes.map((theme) => (
                      <S.ThemeItem
                        key={theme.id}
                        isSelected={form.themeIds.includes(theme.id)}
                        onClick={() => toggleTheme(theme.id)}
                      >
                        {theme.name}
                      </S.ThemeItem>
                    ))
                  )}
                </S.ThemeList>
              </S.Section>

              {/* 저장 / 취소 */}
              <S.FormActions>
                <S.CancelButton
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                >
                  취소
                </S.CancelButton>
                <S.SaveButton onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "저장 중..." : "저장"}
                </S.SaveButton>
              </S.FormActions>
            </>
          ) : (
            /* ── 상세 보기 ── */
            <>
              {/* 이미지 뷰어 */}
              <S.ImageViewer>
                {selectedImage ? (
                  <S.MainImage src={selectedImage} alt={facility.name} />
                ) : (
                  <S.ImagePlaceholder>🏛️</S.ImagePlaceholder>
                )}
              </S.ImageViewer>

              {/* 이미지 썸네일 목록 */}
              {allImages.length > 1 && (
                <S.ImageThumbs>
                  {allImages.map((img, i) => (
                    <S.ThumbItem
                      key={i}
                      src={img}
                      alt={`이미지 ${i + 1}`}
                      isActive={selectedImage === img}
                      onClick={() => setSelectedImage(img)}
                    />
                  ))}
                </S.ImageThumbs>
              )}

              {/* 기본 정보 */}
              <S.Section>
                <S.SectionTitle>기본 정보</S.SectionTitle>
                <S.InfoGrid>
                  <S.InfoRow>
                    <S.InfoLabel>시설명</S.InfoLabel>
                    <S.InfoValue>{facility.name}</S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>카테고리</S.InfoLabel>
                    <S.InfoValue>
                      {CATEGORIES.find((c) => c.value === facility.category)?.label ?? facility.category ?? "—"}
                    </S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>건물</S.InfoLabel>
                    <S.InfoValue>{facility.building?.name ?? "—"}</S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>위치</S.InfoLabel>
                    <S.InfoValue>{facility.location || "—"}</S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>목적</S.InfoLabel>
                    <S.InfoValue>{facility.purpose || "—"}</S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>예약 가능</S.InfoLabel>
                    <S.InfoValue>
                      <S.ReservableBadge reservable={facility.reservable}>
                        {facility.reservable ? "가능" : "불가"}
                      </S.ReservableBadge>
                    </S.InfoValue>
                  </S.InfoRow>
                  {facility.averageStarRating > 0 && (
                    <S.InfoRow>
                      <S.InfoLabel>평균 별점</S.InfoLabel>
                      <S.InfoValue>⭐ {facility.averageStarRating.toFixed(1)}</S.InfoValue>
                    </S.InfoRow>
                  )}
                </S.InfoGrid>
              </S.Section>

              {/* 설명 */}
              {facility.description && (
                <S.Section>
                  <S.SectionTitle>설명</S.SectionTitle>
                  <S.TextBox>{facility.description}</S.TextBox>
                </S.Section>
              )}

              {/* 비품 */}
              {facility.equipment && (
                <S.Section>
                  <S.SectionTitle>비품</S.SectionTitle>
                  <S.TextBox>{facility.equipment}</S.TextBox>
                </S.Section>
              )}

              {/* 주의사항 */}
              {facility.caution && (
                <S.Section>
                  <S.SectionTitle>주의사항</S.SectionTitle>
                  <S.TextBox isWarning>{facility.caution}</S.TextBox>
                </S.Section>
              )}

              {/* 테마 */}
              {facility.themes && facility.themes.length > 0 && (
                <S.Section>
                  <S.SectionTitle>테마</S.SectionTitle>
                  <S.ThemeList>
                    {facility.themes.map((t) => (
                      <S.ThemeItem key={t.id} isSelected={false} style={{ cursor: "default" }}>
                        {t.name}
                      </S.ThemeItem>
                    ))}
                  </S.ThemeList>
                </S.Section>
              )}

              {/* 운영 시간 */}
              {facility.operationTime && facility.operationTime.length > 0 && (
                <S.Section>
                  <S.SectionTitle>운영 시간</S.SectionTitle>
                  <S.OpTimeTable>
                    <S.OpTimeHead>
                      <tr>
                        <S.OpTh>구분</S.OpTh>
                        <S.OpTh>시작</S.OpTh>
                        <S.OpTh>종료</S.OpTh>
                      </tr>
                    </S.OpTimeHead>
                    <tbody>
                      {facility.operationTime.map((t, i) => (
                        <S.OpTr key={i}>
                          <S.OpTd>{t.name}</S.OpTd>
                          <S.OpTd>{t.startTime.slice(0, 5)}</S.OpTd>
                          <S.OpTd>{t.endTime.slice(0, 5)}</S.OpTd>
                        </S.OpTr>
                      ))}
                    </tbody>
                  </S.OpTimeTable>
                </S.Section>
              )}
            </>
          )}
        </S.PanelBody>

      </S.Panel>
    </S.Overlay>
  );
};

export default AdminFacilityDetail;
